/**
 * Keeping a long-lived page current.
 *
 * An app added to a phone's home screen is rarely reloaded: iOS keeps it
 * suspended in the background for days and resumes it where it was. So it
 * goes on running the build it started with long after a deploy, and showing
 * the numbers it fetched when it was last looked at.
 *
 * Two answers, both taken when somebody comes back to the app after a while:
 *
 * - **A new build** (the deployed `version.json` names a different build from
 *   the one running) reloads the page, which is the only way to run it. Over
 *   unsaved work it asks instead, with a Reload button that stays up.
 * - **The same build** re-fetches the screen being looked at, by remounting
 *   it, so what somebody returns to is what is true now.
 *
 * Neither happens over unsaved work (`data-unsaved`, see pullRefresh.ts), on a
 * screen with a camera open, under an open dialog, or on the way back from a
 * file picker: taking a photo for an item hides the app too, and coming back
 * with it is not the moment to remount the screen it is meant for.
 *
 * While the app stays open, a new build is only offered, never forced: the
 * page somebody is using does not reload under their finger.
 */

/** Away for less than this is a glance at another app, not a return. */
export const AWAY_MS = 60_000;

/** While the app stays open and visible, it looks for a new build this often. */
export const CHECK_EVERY_MS = 30 * 60_000;

export const RUNNING_BUILD: string = import.meta.env.VITE_BUILD_ID ?? "dev";

/**
 * True when a different build is deployed than the one running. Any failure --
 * offline, dev server, a body that is not the file -- reads as "no", because a
 * wrong "yes" reloads somebody's page for nothing.
 */
export async function newBuildDeployed(
  fetcher: typeof fetch = fetch,
  running: string = RUNNING_BUILD,
): Promise<boolean> {
  if (running === "dev") return false;
  try {
    const response = await fetcher("/version.json", { cache: "no-store" });
    if (!response.ok) return false;
    const body = (await response.json()) as { build?: unknown };
    return typeof body.build === "string" && body.build !== "" && body.build !== running;
  } catch {
    return false;
  }
}

export type ReturnAction = "nothing" | "refetch" | "reload" | "offer-reload";

/**
 * What coming back should do, given how long the app was away, whether a new
 * build is out, and whether the page is holding something it must not lose.
 */
export function onReturn({
  awayMs,
  newBuild,
  busy,
}: {
  awayMs: number;
  newBuild: boolean;
  busy: boolean;
}): ReturnAction {
  if (awayMs < AWAY_MS) return "nothing";
  if (newBuild) return busy ? "offer-reload" : "reload";
  return busy ? "nothing" : "refetch";
}

/** The page is holding something a reload or a remount would throw away. */
export const pageIsBusy = (): boolean =>
  document.querySelector('[data-unsaved], [aria-modal="true"], .scan-view, .menu-scrim') !== null;

/**
 * Put the page back where it was after a remount. The screen comes back as a
 * skeleton and grows as its data lands, so this keeps trying until the page
 * is tall enough to scroll that far, or a couple of seconds have passed --
 * or somebody scrolls first, which wins.
 */
export function restoreScroll(y: number, giveUpMs = 2000): void {
  if (y <= 0) return;
  const until = Date.now() + giveUpMs;
  let moved = false;
  const stop = () => (moved = true);
  window.addEventListener("touchstart", stop, { once: true, passive: true });
  window.addEventListener("wheel", stop, { once: true, passive: true });
  const step = () => {
    if (moved) return;
    window.scrollTo(0, y);
    if (Math.abs(window.scrollY - y) > 1 && Date.now() < until) window.requestAnimationFrame(step);
    else {
      window.removeEventListener("touchstart", stop);
      window.removeEventListener("wheel", stop);
    }
  };
  window.requestAnimationFrame(step);
}
